import { authenticateHCP } from "data/hcp-auth";
import React, { useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import ga4 from "react-ga4";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCountry } from "data/countryContext";
import { setUserProperty } from "data/analytics";

const API_BASE = "https://hcp.cognipharma.com/api-pharmacist-icyclin.php";

const InternalLogin = () => {
	const { t } = useTranslation();
	const { country } = useCountry();
	const [name, setName] = useState("");
	const [job, setJob] = useState("doctor");
	const [licenseNumber, setLicenseNumber] = useState("");
	const [isLoggingIn, setIsLoggingIn] = useState(false);
	const [showSupport, setShowSupport] = useState(false);

	const [fallbackMode, setFallbackMode] = useState(false);
	const [fallbackEmail, setFallbackEmail] = useState("");

	const navigate = useNavigate();

	const nameChange = (e) => {
		setName(e.target.value);
	};

	// Helper to normalize and remove diacritics from a string
	const normalizeName = (str) =>
		str
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.trim()
			.toLowerCase();

	const loginUser = async (e) => {
		// Prevent Default to ensure form is not submitted (would cause failure to fetch)
		e.preventDefault();

		// State to update buttons
		setIsLoggingIn(true);

		console.log("Logging in with " + name + " " + job + " " + licenseNumber);
		ga4.event({
			category: "app-interaction",
			action: "login-submit",
			label: job,
			job: job,
		});

		// Object containing valid users with licenseNumber, name, and job
		const validUsers = [
			/*{ licenseNumber: "23224", name: "Alice", job: "doctor" },
			{ licenseNumber: "12345", name: "Bob", job: "nurse" },
			{ licenseNumber: "67890", name: "Carol", job: "pharmacist" }*/
			{ licenseNumber: "23224", name: "Cognipharma", job: "doctor" },
			{
				licenseNumber: "23841",
				name: "Dolores Maria Corôa Prudêncio",
				job: "pharmacist",
			},
			{
				licenseNumber: "09475",
				name: "Cristina Rita Figueiredo Raposo Barreiras",
				job: "pharmacist",
			},
			// add more users as needed
		];

		if (country !== "PT") {
			// Non-PT users are not validated and can access the app directly with button click
			localStorage.removeItem("cogni-user-id");
			localStorage.removeItem("login-token");
			localStorage.setItem("cogni-user-id", name.toLowerCase());
			localStorage.setItem(
				"login-token",
				"doctor|00000|Non-PT User",
			);
			localStorage.setItem("hcp-specialty", "N/A");
			localStorage.setItem("hcp-all-specialties", "N/A");
			const redirectTo = sessionStorage.getItem("redirectTo") ?? "/";
			setUserProperty("hcp_type", job);
			setUserProperty("hcp_specialty", "N/A");
			setUserProperty("hcp_specialties", "N/A");
			ga4.event({
				category: "app-interaction",
				action: "login-success",
				label: job,
				job: job,
				hcp_specialty: "N/A",
				hcp_specialties: "N/A",
			});
			navigate(redirectTo);
			return;
		}

		try {
			// Fetch entries from the API
			const res = await fetch(`${API_BASE}?action=entries`);
			const data = await res.json();

			//console.log("Fetched entries:", data);
			const isValidUser = Object.entries(data).some(([key, value]) => {
				//console.log("Checking entry:", value.number, value.job, value.name);
				const matchesLicense = value.number === licenseNumber;
				const matchesJob = value.job === job;

				const inputNameWords = normalizeName(name).split(/\s+/);
				const dbNameWords = normalizeName(value.name).split(/\s+/);
				const nameMatches = inputNameWords.every((w) =>
					dbNameWords.includes(w),
				);

				return matchesLicense && matchesJob && nameMatches;
			});

			if (isValidUser) {
				localStorage.removeItem("cogni-user-id");
				localStorage.removeItem("login-token");
				localStorage.setItem("cogni-user-id", name.toLowerCase());
				localStorage.setItem(
					"login-token",
					job + "|" + licenseNumber + "|" + name,
				);
				localStorage.setItem("hcp-specialty", "Farmacêutico");
				localStorage.setItem("hcp-all-specialties", "Farmacêutico");
				const redirectTo = sessionStorage.getItem("redirectTo") ?? "/";
				setUserProperty("hcp_type", job);
				setUserProperty("hcp_specialty", "Farmacêutico");
				setUserProperty("hcp_specialties", "Farmacêutico");
				ga4.event({
					category: "app-interaction",
					action: "login-success",
					label: job,
					job: job,
					hcp_specialty: "Farmacêutico",
					hcp_specialties: "Farmacêutico",
				});
				navigate(redirectTo);
				return;
			}
		} catch (err) {
			console.error("Validation via Redis failed:", err);
		}

		/*
		// Fallback if the above stops working
		const isValidUser = validUsers.some((user) => {
			// Normalize and split names into arrays of lowercase words (diacritics removed)
			const inputNameWords = normalizeName(name).split(/\s+/);
			const userNameWords = normalizeName(user.name).split(/\s+/);

			// Check if every word in inputNameWords exists in userNameWords
			const nameMatches = inputNameWords.every((word) =>
			userNameWords.includes(word)
			);

			return (
			user.licenseNumber === licenseNumber && nameMatches && user.job === job
			);
		});

		if (isValidUser) {
			localStorage.removeItem("cogni-user-id");
			localStorage.removeItem("login-token");
			localStorage.setItem("cogni-user-id", name.toLowerCase());
			localStorage.setItem(
				"login-token",
				job + "|" + licenseNumber + "|" + name
			);
			let redirectTo = sessionStorage.getItem("redirectTo") ?? "/";
			ga4.event({
				category: "app-interaction",
				action: "login-success",
				label: job,
				job,
			});
			navigate(redirectTo);
		} else {
		*/

		// Pharmacist pre-registration and notification
		if (job === "pharmacist") {
			if (name === "" || licenseNumber === "") {
				setIsLoggingIn(false);
				alert(
					"Impossível validar os seus dados." +
					"\n\nPor favor verifique e tente de novo.",
				);
				setShowSupport(true);
				return;
			} else {
				setFallbackMode(true);
				return;
			}
		}

		// Fallback if not pharmacist or not in whitelist
		authenticateHCP(job, name, licenseNumber).then(
			(result) => {
				if (result.isValid) {
					const redirectTo = sessionStorage.getItem("redirectTo") ?? "/";
					const hcpSpecialty = localStorage.getItem("hcp-specialty") || "N/A";
					const hcpAllSpecialties = localStorage.getItem("hcp-all-specialties") || "N/A";
					setUserProperty("hcp_type", job);
					setUserProperty("hcp_specialty", hcpSpecialty);
					setUserProperty("hcp_specialties", hcpAllSpecialties);
					ga4.event("login-success", {
						event_category: "app-interaction",
						event_label: job,
						job: job,
						hcp_specialty: hcpSpecialty,
						hcp_specialties: hcpAllSpecialties,
					});
					//ga4.event({
					//	category: "app-interaction",
					//	action: "login-success",
					//	label: job,
					//	job: job,
					//	hcp_specialty: hcpSpecialty,
					//	hcp_specialties: hcpAllSpecialties,
					//});
					navigate(redirectTo);
				} else {
					ga4.event({
						category: "app-interaction",
						action: "login-not-valid",
						label: job,
						message: result.validationMessage,
						licenseNumber,
					});
					setIsLoggingIn(false);
					alert(
						"Impossível validar os seus dados no " +
						result.datasource +
						": " +
						result.validationMessage +
						"\n\nPor favor verifique e tente de novo.",
					);
					setShowSupport(true);
				}
			},
			(err) => {
				ga4.event({
					category: "app-interaction",
					action: "login-error",
					label: job,
					message: err,
					licenseNumber,
				});
				setIsLoggingIn(false);
				alert("Erro na validação: " + err);
				setShowSupport(true);
			},
		);
	};

	return (
		<div className="text-white d-flex align-items-center justify-content-center flex-column text-center mt-0 mt-md-5">
			<div className="bg-white rounded p-5 text-center">
				<h2 className="m-0 ms-lg-4  text-center">
					{t(
						"auto.acessoExclusivoAProfissionaisDeSaude",
						"Acesso exclusivo a profissionais de saúde",
					)}
				</h2>
				{country !== "PT" ? (
					<p className="small text-muted text-center my-5">
						{t(
							"auto.porFavorConfirmeQueEUmProfissionalDeSaudeCarregandoNoBotaoAbaixo",
							"Por favor confirme que é um profissional de saúde, carregando no botão abaixo.",
						)}
						<br />
						{t(
							"auto.soTeraDeFazerEstaValidacaoUmaUnicaVezNesteDispositivo",
							"Só terá de fazer esta validação uma única vez, neste dispositivo.",
						)}
					</p>
				) : (
					<p className="small text-muted text-center mb-4">
						{t(
							"auto.osSeusDadosProfissionaisSeraoValidadosNaRespetivaOrdemDosMedicosFarmaceuticosOuEnfermeiros",
							"Os seus dados profissionais serão validados na respetiva Ordem (dos\n          Médicos, Farmacêuticos ou Enfermeiros).",
						)}
						<br />
						{t(
							"auto.soTeraDeFazerEstaValidacaoUmaUnicaVezNesteDispositivo",
							"Só terá de fazer esta validação uma única vez, neste dispositivo.",
						)}
					</p>
				)}
				<div className="d-flex  align-items-center flex-column text-secondary">
					{country !== "PT" ? (
						<div className="space-y-4 my-5">
							<Button onClick={loginUser} className="btn btn-primary btn-lg">
								{isLoggingIn ? (
									<>
										<Spinner
											animation="border"
											role="status"
											aria-hidden="true"
											size="sm"
											as="span"
											className="me-2"
										></Spinner>
										{t("auto.aValidar", "A validar...")}
									</>
								) : (
									t(
										"auto.souUmProfissionalDeSaude",
										"Sou um profissional de saúde",
									)
								)}
							</Button>
						</div>
					) : fallbackMode ? (
						<div className="space-y-4 my-5">
							<p>
								<strong>
									{t(
										"auto.naoFoiPossivelValidarOsSeusDadosAutomaticamente",
										"Não foi possível validar os seus dados automaticamente.",
									)}
								</strong>
							</p>
							<p>
								{t(
									"auto.porFavorIntroduzaOSeuEnderecoDeEmailParaQuePossamosContactaloa",
									"Por favor introduza o seu endereço de email para que possamos\n                contactá-lo/a.",
								)}
							</p>
							<input
								type="email"
								placeholder={t("auto.oSeuEmail")}
								value={fallbackEmail}
								onChange={(e) => setFallbackEmail(e.target.value)}
								className="p-2 w-full m-2"
							/>
							<button
								className="btn btn-primary btn-lg btn btn-primary m-2"
								onClick={async () => {
									const res = await fetch(
										"https://hcp.cognipharma.com/email/send-notification-mail.php",
										{
											method: "POST",
											headers: { "Content-Type": "application/json" },
											body: JSON.stringify({
												fallbackEmail,
												job,
												licenseNumber,
												name,
											}),
										},
									);
									const data = await res.json();
									alert(data.message || "Request submitted");
									setFallbackEmail("");
									setFallbackMode(false);
									setIsLoggingIn(false);
								}}
							>
								{t("auto.submeter", "Submeter")}
							</button>
						</div>
					) : (
						<Form onSubmit={loginUser}>
							<fieldset
								disabled={isLoggingIn}
								className="d-flex  align-items-center justify-content-center flex-column"
							>
								<Form.Group className="mb-3" controlId="name">
									<Form.Label>{t("auto.nome", "Nome")}</Form.Label>
									<Form.Control
										type="text"
										value={name}
										onChange={nameChange}
										className="text-center"
										style={{ width: "90vw", maxWidth: "400px" }}
									/>
								</Form.Group>
								<Form.Group className="mb-3" controlId="job">
									<Form.Label>{t("auto.profissao", "Profissão")}</Form.Label>
									<Form.Select
										aria-label="Profissão"
										value={job}
										onChange={(e) => {
											setJob(e.target.value);
										}}
										className="text-center"
										style={{ width: "90vw", maxWidth: "400px" }}
									>
										<option value="doctor">
											{t("auto.medicoa", "Médico/a")}
										</option>
										<option value="nurse">
											{t("auto.enfermeiroa", "Enfermeiro/a")}
										</option>
										<option value="pharmacist">
											{t("auto.farmaceuticoa", "Farmacêutico/a")}
										</option>
									</Form.Select>
								</Form.Group>

								<Form.Group className="mb-3" controlId="licenseNumber">
									<Form.Label>{t("auto.nDeOrdem", "Nº de Ordem")}</Form.Label>
									<Form.Control
										placeholder="_ _ _ _ _ _"
										type="text"
										value={licenseNumber}
										maxLength={6}
										onChange={(e) => {
											const cleanedLicenseNumber = e.target.value.replace(
												/^0+/,
												"",
											);
											setLicenseNumber(cleanedLicenseNumber);
										}}
										className="text-center"
										style={{ width: "200px" }}
									/>
								</Form.Group>
							</fieldset>
							<div className="mt-3">
								<Button type="submit" className="btn btn-primary btn-lg">
									{isLoggingIn ? (
										<>
											<Spinner
												animation="border"
												role="status"
												aria-hidden="true"
												size="sm"
												as="span"
												className="me-2"
											></Spinner>
											{t("auto.aValidar", "A validar...")}
										</>
									) : (
										t("auto.validar", "Validar")
									)}
								</Button>
							</div>
						</Form>
					)}
					{showSupport && (
						<div
							className="small text-muted text-center mt-4"
							style={{ maxWidth: "300px" }}
						>
							{t(
								"auto.emCasoDeDificuldadeNoAcessoComOsSeusDadosProfissionaisContacteO",
								"Em caso de dificuldade no acesso com os seus dados profissionais\n              contacte o",
							)}
							<Link to="/help#support">{t("auto.suporte", "suporte")}</Link>.
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
export default InternalLogin;
